import { action, computed } from 'mobx'
import { task } from 'mobx-task'
import type { Member } from '@/features/iam/members/Member'
import type {
  AlertErrorHandlerFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { RoomViewModel } from '@/features/messaging/rooms/RoomViewModel'
import type { GetAddToCalendarDelegate } from '@/features/shell/platform/types'
import type { NavigateFn } from '@/RootStore'
import { links } from '@/utils/link'
import { dateFormatters } from '@/utils/date-util'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { type ConsultationStore } from '@/features/jobs/consultations/ConsultationStore'
import { type ConsultationIncompleteReason } from '@/features/jobs/consultations/Consultation'
import { type ConsultationPendingBookingModalViewModel } from '@/features/jobs/details/components/ConsultationPendingBookingModal/ConsultationPendingBookingModalViewModel'
import {
  canPickConsultationTime,
  consultationState,
  consultationSteps,
} from '@/features/jobs/details/consultation-state'
import { type IncompleteConsultationCompletionDialogViewModel } from '@/features/jobs/details/components/IncompleteConsultationCompletionDialog/IncompleteConsultationCompletionDialogViewModel'

/**
 * A job's consultation call: scheduling, joining and completing it.
 */
export class JobConsultationViewModel {
  constructor(
    private readonly _job: Job,
    private readonly _getJobWorkspaceMember: () => Member,
    private readonly _jobStore: JobStore,
    private readonly _consultationStore: ConsultationStore,
    private readonly _navigate: NavigateFn,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _getAddToCalendarDelegate: GetAddToCalendarDelegate,
    public readonly roomVm: RoomViewModel,
    public readonly consultationPendingBookingModalVm: ConsultationPendingBookingModalViewModel,
    public readonly incompleteConsultationCompletionDialogVm: IncompleteConsultationCompletionDialogViewModel,
  ) {}

  @computed
  get hasConsultation() {
    return this._job.hasActiveConsultation
  }

  @computed
  get consultationIsPendingScheduling() {
    return (
      this._job.consultation?.isPendingBooking ||
      this._job.consultation?.isPendingAvailability
    )
  }

  /**
   * Whether the consultation call is open to join.
   */
  @computed
  get canJoinRoom() {
    return Boolean(this.roomVm.activeRoom && !this.roomVm.isRoomClosed)
  }

  @computed
  get isConsultationBookedAndActive() {
    return (
      this._job.consultation?.bookedTime &&
      (this._job.consultation.status === 'PendingCall' ||
        this._job.consultation.status === 'InProgress')
    )
  }

  @computed
  get consultationPendingSchedulingText() {
    return this._job.consultation?.isPendingBooking
      ? 'Pending consultation scheduling'
      : this._job.consultation?.isPendingAvailability
        ? 'Customer availability selection pending'
        : ''
  }

  @computed
  get previouslyBooked() {
    return this._job.consultation?.previouslyBooked
  }

  /**
   * Whether this viewer is the one who has to pick the time.
   */
  @computed
  get canPickTime() {
    return canPickConsultationTime(this._job)
  }

  /**
   * Where the call stands, as the step line and summary show it.
   */
  @computed
  get state() {
    return consultationState(this._job)
  }

  /**
   * Which call step the consultation is on, e.g. "Step 2 of 4 · Call".
   */
  @computed
  get stepLabel() {
    const state = this.state
    if (!state) {
      return ''
    }

    return `Step ${state.step + 1} of ${consultationSteps.length} · ${consultationSteps[state.step]}`
  }

  @computed
  get consultationCallTimeText() {
    const consultation = this._job.consultation
    if (consultation?.skipScheduling) {
      return 'Message your Client to schedule a consultation call'
    }
    if (this.consultationIsPendingScheduling) {
      return this.consultationPendingSchedulingText
    }
    const bookedStartTime = consultation?.bookedTime?.startTime
    return bookedStartTime
      ? dateFormatters.dateTimeWithZone.format(bookedStartTime)
      : undefined
  }

  @computed
  get canFulfillConsultation() {
    return this._getJobWorkspaceMember() !== this._job.championClient?.member
  }

  /**
   * Whether consultation card is visible
   */
  @computed
  get showConsultationCard() {
    return this.hasConsultation && this.canFulfillConsultation
  }

  /**
   * Whether call card is always visible
   */
  @computed
  get showCallCard() {
    return (
      this._job.alwaysEnableVoiceCall &&
      !this.showConsultationCard &&
      this._job.status !== 'UnderConstruction' &&
      this._job.status !== 'Closed'
    )
  }

  /**
   * Requests closing the job when the job has an incomplete consultation.
   */
  @action.bound
  async requestCloseWithIncompleteConsultation(
    consultationIncompleteReason: ConsultationIncompleteReason,
    consultationIncompleteOtherReason?: string,
  ) {
    if (
      !consultationIncompleteReason ||
      (consultationIncompleteReason === 'Other' &&
        !consultationIncompleteOtherReason)
    ) {
      return
    }

    await this._jobStore
      .close(this._job, {
        reason: consultationIncompleteReason,
        otherReason: consultationIncompleteOtherReason,
      })
      .catch(this._createAlertErrorHandler('Closing the job failed'))
  }

  promptForTimeSlotSelection = task.resolved(async () => {
    if (!this._job.consultation?.isPendingBooking) {
      return
    }

    const bookedConsultations = await this._createAlertRetryWrapper(
      'Loading booked consultations failed',
    )(() => this._consultationStore.fetchBookedConsultations())

    const response =
      await this.consultationPendingBookingModalVm.promptForTimeSlots(
        {
          availability: () => this._job.consultation!.availability,
          bookedConsultations: () => bookedConsultations,
          requestedLength: () => this._job.consultation!.requestedLength,
          clientMember: this._job.championClient!.member,
          currentMember: this._getJobWorkspaceMember(),
        },
        this._job.jobType,
      )
    if (response == null) {
      return
    }

    if (response.type === 'Book') {
      await this._consultationStore
        .bookConsultation(this._job.id, response.selectedTimeSlot)
        .catch(this._createAlertErrorHandler('Booking the consultation failed'))
      return
    }

    await this._consultationStore
      .unbookConsultation(this._job.id)
      .catch(this._createAlertErrorHandler('Unbooking the consultation failed'))
    this._navigate(links.opportunities())
  })

  downloadConsultationCalendar = task.resolved(async () => {
    if (!this.isConsultationBookedAndActive) {
      return
    }

    const file = await this._consultationStore
      .fetchConsultationCalendar(this._job.id)
      .catch(
        this._createAlertErrorHandler('Downloading the calendar event failed'),
      )

    this._getAddToCalendarDelegate().addToCalendar(file)
  })
}
